import {
  type PrecomputedEvaluationsInterface,
  useExperiment as useExperimentStatsig,
  useStatsigClient,
} from '@statsig/react-bindings';
import { type z } from 'zod';
import { reportMessage, zodIssueMessages } from '@/helpers/errors/report';
import { isStatsigClientReady } from '@/helpers/statsig';

export const createExperiment = <T>(key: string, schema: z.ZodType<T>, mainDefaultValue: T) =>
  function getExperimentInner(client: PrecomputedEvaluationsInterface | undefined, defaultValue = mainDefaultValue) {
    if (!isStatsigClientReady(client)) {
      return defaultValue;
    }

    const experiment = client.getExperiment(key);
    if (experiment.value === undefined || Object.keys(experiment.value).length === 0) {
      return defaultValue;
    }

    const result = schema.safeParse(experiment.value);
    if (result.error) {
      reportMessage('Invalid experiment config', {
        domain: 'flags',
        level: 'warning',
        extra: { key, issues: zodIssueMessages(result.error) },
      });
      return defaultValue;
    }

    return result.data;
  };

export const createExperimentHook = <T>(key: string, schema: z.ZodType<T>, mainDefaultValue: T) => {
  function useExperiment(defaultValue = mainDefaultValue) {
    const { client } = useStatsigClient();
    const experiment = useExperimentStatsig(key);

    if (!isStatsigClientReady(client) || experiment.value === undefined) {
      return defaultValue;
    }

    const result = schema.safeParse(experiment.value);

    if (result.error) {
      reportMessage('Invalid experiment config', {
        domain: 'flags',
        level: 'warning',
        extra: { key, issues: zodIssueMessages(result.error) },
      });
      return defaultValue;
    }

    return result.data;
  }

  Object.defineProperties(useExperiment, {
    value: {
      get value() {
        return key;
      },
      configurable: false,
      enumerable: false,
      writable: false,
    },
    schema: {
      get value() {
        return schema;
      },
      configurable: false,
      enumerable: false,
      writable: false,
    },
  });

  return useExperiment as typeof useExperiment & {
    get value(): string;
    get schema(): z.ZodType<T>;
  };
};
